import React, { useState } from "react";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import { DOW, addDays, sameDay, startOfDay, fmtRange, entryMeta, initials, hrsBetween } from "./scheduleUtils";
import { offForDay, OffSummary, TimeOffCard } from "./WeekCalendar";

const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);

// Mini shift card used inside the swimlane cells. ctx="row" means the row IS
// the client/facility (so the card leads with the shift kind + staff); ctx="staff"
// uses the full category meta (title + pill + colour) like the All view.
function ShiftCard({ shift, ctx, onClick, onHover, onLeave }) {
  const meta = entryMeta(shift);
  const open = shift.status === "open";
  const recurring = shift.recurrence?.freq && shift.recurrence.freq !== "none";
  let cat;
  let name;
  let pill = null;
  let desc;
  if (ctx === "row") {
    const isFac = shift.type === "facility";
    const appt = shift.kind === "appointment";
    const typeLabel = isFac ? "Facility Shift" : "Client Visit";
    // Prefer the backend-joined clientName (covers multi-client shifts), then
    // the decrypted form1 name, then whatever entryMeta could derive.
    const clientName =
      shift.clientName ||
      (shift.client?.form1
        ? `${shift.client.form1.firstName || ""} ${shift.client.form1.lastName || ""}`.trim()
        : "") ||
      meta.clientName ||
      "";
    const staffName = shift.staff?.name || "";
    // Colour by kind: appointments green, shifts (client or facility) purple,
    // open amber.
    cat = open ? "open" : appt ? "facility" : "client";
    // Appointments lead with their title; shifts lead with the type label.
    name = open ? "Open shift" : appt ? shift.apptTitle || typeLabel : typeLabel;
    if (open) {
      // Show which facility the open shift is at, plus the role/coverage note.
      const parts = [];
      if (meta.facility) parts.push(meta.facility);
      parts.push(shift.open?.role ? `${shift.open.role} needed` : "Unassigned");
      desc = parts.join(" · ");
    } else {
      // Descriptive subtitle: (type for appts) · facility · client · assignee —
      // never a bare "Unassigned".
      const parts = [];
      if (appt) parts.push(typeLabel);
      if (meta.facility) parts.push(meta.facility);
      if (clientName) parts.push(clientName);
      if (staffName) parts.push(staffName);
      desc = parts.join(" · ") || typeLabel;
    }
  } else {
    cat = meta.cat === "open" ? "open" : meta.appt ? "facility" : "client";
    name = meta.title;
    pill = meta.pill;
    desc = shift.type === "facility" ? meta.facility : meta.clientName || meta.facility;
  }
  const catCls = cat === "client" ? css.catclient : cat === "facility" ? css.catfacility : cat === "open" ? css.catopen : css.catclient;
  return (
    <button
      type="button"
      className={`${css.shiftCard} ${catCls}`}
      onClick={() => onClick?.(shift)}
      onMouseEnter={(e) => onHover?.(shift, e)}
      onMouseMove={(e) => onHover?.(shift, e)}
      onMouseLeave={() => onLeave?.()}
    >
      {pill ? <span className={css.sPill}>{pill}</span> : null}
      <span className={css.sName}>
        {name}
        {recurring ? <Icon name="repeat" size={11} className={css.recurIc} /> : null}
      </span>
      <span className={css.sTime}>{shift.allDay ? "All day" : fmtRange(shift.startMin, shift.endMin)}</span>
      {desc ? <span className={css.sMeta}>{desc}</span> : null}
    </button>
  );
}

export default function GroupedWeekCalendar({ shifts = [], timeOff = [], weekStart, groupBy, staff = [], facilities = [], clients = [], onShiftClick, onHover, onLeave, dropArmed = false, onDayDrop }) {
  const [collapsed, setCollapsed] = useState(false);
  // Quick-build drop: which day cell is currently being dragged over.
  const [dropKey, setDropKey] = useState(null);

  // Day-cell drop handlers, active only while a quick-build chip is armed.
  const cellDropProps = (d, key) =>
    dropArmed
      ? {
          onDragOver: (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
            if (dropKey !== key) setDropKey(key);
          },
          onDragLeave: (e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setDropKey((p) => (p === key ? null : p));
            }
          },
          onDrop: (e) => {
            e.preventDefault();
            setDropKey(null);
            let payload = null;
            try {
              const raw = e.dataTransfer.getData("application/json");
              if (raw) payload = JSON.parse(raw);
            } catch (err) {
              /* ignore malformed payload */
            }
            onDayDrop?.(d, payload);
          },
        }
      : {};
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const now = new Date();
  const items = shifts.map((s) => ({ ...s, _date: startOfDay(new Date(s.date)) }));
  const isAll = groupBy === "all";
  const isClients = groupBy === "client";
  const isFacility = groupBy === "facility";
  const headLabel = isAll ? "Schedule" : isClients ? "Clients" : isFacility ? "Facilities" : "Staff";
  const headIcon = isAll ? "calendar" : isClients ? "person" : isFacility ? "hospital" : "user";

  let entities = [];
  if (isAll) {
    // Ungrouped "All" list: a single row holding every shift, stacked per day
    // (same card layout as the grouped views, but with no time breakdown).
    entities = [
      {
        id: "all",
        kind: "all",
        name: "All shifts",
        av: initials("All"),
        sub: `${items.length} shift${items.length !== 1 ? "s" : ""} this week`,
        shiftsFor: (d) => items.filter((s) => sameDay(s._date, d)).sort((a, b) => a.startMin - b.startMin),
      },
    ];
  } else if (isFacility) {
    entities = facilities.map((f) => {
      // include open shifts here so an unfilled facility shift sits on its facility/day
      const rowShifts = items.filter((s) => s.type === "facility" && idOf(s.facility) === f.value);
      return {
        id: `f:${f.value}`,
        kind: "fac",
        name: f.label,
        av: initials(f.label),
        sub: `${rowShifts.length} shift${rowShifts.length !== 1 ? "s" : ""} this week`,
        shiftsFor: (d) => rowShifts.filter((s) => sameDay(s._date, d)).sort((a, b) => a.startMin - b.startMin),
      };
    });
  } else if (isClients) {
    entities = clients.map((c) => {
      // include open shifts here so an unfilled client visit sits on its client/day
      const rowShifts = items.filter((s) => s.type === "client" && idOf(s.client) === c.value);
      return {
        id: `c:${c.value}`,
        kind: "client",
        name: c.label,
        av: initials(c.label),
        photo: c.photo || null,
        sub: `${rowShifts.length} visit${rowShifts.length !== 1 ? "s" : ""} this week`,
        shiftsFor: (d) => rowShifts.filter((s) => sameDay(s._date, d)).sort((a, b) => a.startMin - b.startMin),
      };
    });
  } else {
    entities = staff.map((st) => {
      const rowShifts = items.filter((s) => s.status !== "open" && idOf(s.staff) === st.value);
      const mins = rowShifts.reduce((a, s) => a + (s.allDay ? 0 : hrsBetween(s.startMin, s.endMin)), 0);
      const ot = mins > 40;
      return {
        id: st.value,
        kind: "staff",
        name: st.label,
        av: initials(st.label),
        photo: st.photo || null,
        sub: `${mins.toFixed(mins % 1 ? 1 : 0)}h scheduled${ot ? " · OT" : ""}`,
        ot,
        shiftsFor: (d) => rowShifts.filter((s) => sameDay(s._date, d)).sort((a, b) => a.startMin - b.startMin),
      };
    });
  }

  // The separate "Open Shifts" row only makes sense in Staff view (open shifts have
  // no staff). In Client/Facility views the open shift sits on its own entity row.
  const hideOpen = isClients || isFacility || isAll;
  const cardCtx = isClients || isFacility || isAll ? "row" : "staff";
  const openByDay = (d) => items.filter((s) => s.status === "open" && sameDay(s._date, d)).sort((a, b) => a.startMin - b.startMin);
  const hasOpen = items.some((s) => s.status === "open");

  return (
    <div className={css.wkcalScroll}>
      <div className={`${css.weekGrid2} ${collapsed ? css.collapsed : ""} ${isAll ? css.wgAll : ""}`}>
        {/* header row */}
        <div className={`${css.wgCell} ${css.wgCorner}`}>
          <span className={css.wgCornerLabel}>
            <Icon name={headIcon} size={15} /> {headLabel} <span className={css.wgCornerCount}>{entities.length}</span>
          </span>
          <button
            type="button"
            className={css.wgCollapse}
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand names" : "Collapse names"}
            title={collapsed ? "Expand" : "Collapse"}
          >
            <Icon name={collapsed ? "chevronsRight" : "chevronsLeft"} size={14} />
          </button>
        </div>
        <div className={`${css.wgCell} ${css.wgGutter}`} />
        {days.map((d, i) => (
          <div key={d.toISOString()} className={`${css.wgCell} ${css.wgDayhead} ${sameDay(d, now) ? css.today : ""} ${i === 6 ? css.lastCol : ""}`}>
            <div className={css.dow}>{DOW[d.getDay()]}</div>
            <div className={css.dnum}>{d.getDate()}</div>
            {/* Time off shows on every grouping except Clients (it's staff-based). */}
            {!isClients && <OffSummary items={offForDay(timeOff, d)} />}
          </div>
        ))}

        {/* entity rows */}
        {entities.map((en) => (
          <React.Fragment key={en.id}>
            <div className={`${css.wgCell} ${css.wgStaff}`}>
              <span className={`${css.wgAv} ${en.kind === "client" ? css.client : en.kind === "fac" ? css.fac : ""}`}>
                {en.photo ? <img src={en.photo} alt="" /> : en.kind === "fac" ? <Icon name="hospital" size={18} /> : en.av}
              </span>
              <div className={css.wgStinfo}>
                <div className={css.wgNm}>{en.name}</div>
                <div className={`${css.wgSub} ${en.ot ? css.ot : ""}`}>{en.sub}</div>
              </div>
            </div>
            <div className={`${css.wgCell} ${css.wgGutter}`} />
            {days.map((d, i) => {
              const wknd = d.getDay() === 0 || d.getDay() === 6;
              const key = `${en.id}-${i}`;
              return (
                <div
                  key={d.toISOString()}
                  className={`${css.wgCell} ${css.wgDay} ${wknd ? css.weekend : ""} ${i === 6 ? css.lastCol : ""} ${dropArmed ? css.wgDayDroppable : ""} ${dropKey === key ? css.wgDayDropHover : ""}`}
                  {...cellDropProps(d, key)}
                >
                  {/* Time off as a calendar item (staff name + approval status).
                      "All" view shows everyone's; the Staff view shows only that
                      staff's. Client/Facility groupings keep the header chip only,
                      since time off is staff-based. */}
                  {(isAll || en.kind === "staff") &&
                    offForDay(timeOff, d)
                      .filter((t) => isAll || idOf(t.staff) === en.id)
                      .map((t) => <TimeOffCard key={idOf(t)} item={t} />)}
                  {en.shiftsFor(d).map((s) => (
                    <ShiftCard key={idOf(s)} shift={s} ctx={cardCtx} onClick={onShiftClick} onHover={onHover} onLeave={onLeave} />
                  ))}
                </div>
              );
            })}
          </React.Fragment>
        ))}

        {/* open shifts row */}
        {!hideOpen && hasOpen && (
          <>
            <div className={`${css.wgCell} ${css.wgStaff}`}>
              <span className={`${css.wgAv} ${css.open}`}><Icon name="calendar" size={18} /></span>
              <div className={css.wgStinfo}>
                <div className={css.wgNm}>Open Shifts</div>
                <div className={css.wgSub}>Unassigned</div>
              </div>
            </div>
            <div className={`${css.wgCell} ${css.wgGutter}`} />
            {days.map((d, i) => {
              const key = `open-${i}`;
              return (
                <div
                  key={d.toISOString()}
                  className={`${css.wgCell} ${css.wgDay} ${i === 6 ? css.lastCol : ""} ${dropArmed ? css.wgDayDroppable : ""} ${dropKey === key ? css.wgDayDropHover : ""}`}
                  {...cellDropProps(d, key)}
                >
                  {openByDay(d).map((s) => (
                    <ShiftCard key={idOf(s)} shift={s} ctx={cardCtx} onClick={onShiftClick} onHover={onHover} onLeave={onLeave} />
                  ))}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
